import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

// Sample blog post data - replace with your actual data source
const blogPostsData: { [key: string]: any } = {
  "1": {
    id: 1,
    title: "How AI Receptionists Are Transforming Small Businesses in South Africa",
    category: "Business Growth",
    author: "Sarah Mitchell",
    authorBio: "Business consultant specializing in digital transformation for SMEs",
    date: "Feb 10, 2026",
    readTime: "5 min read",
    image: "/images/blog-1.jpg",
    content: `
      <p>In today's competitive business landscape, small and medium-sized businesses in South Africa face a unique challenge: providing exceptional customer service while managing limited resources. Enter AI-powered phone answering systems—a game-changing technology that's leveling the playing field.</p>

      <h2>The Challenge for SA Small Businesses</h2>
      <p>Research shows that 80% of missed calls never result in a callback. For a small business, this translates to thousands of rands in lost revenue annually. Traditional solutions like hiring additional staff or expensive call centers aren't viable for most SMEs operating on tight margins.</p>

      <h2>How AI Receptionists Work</h2>
      <p>AI-powered phone systems use advanced natural language processing to understand caller intent, respond appropriately, and capture essential information. Unlike traditional IVR systems that frustrate callers with button-pressing menus, modern AI receptionists conduct natural conversations.</p>

      <blockquote>
        "Since implementing Gerbo, we've captured 35% more leads from after-hours calls. It's like having a tireless team member who never takes a break." — Thabo Mkhize, Estate Agent, Cape Town
      </blockquote>

      <h2>Real Benefits for South African Businesses</h2>
      <ul>
        <li><strong>24/7 Availability:</strong> Capture leads even when your office is closed</li>
        <li><strong>Cost-Effective:</strong> Fraction of the cost of hiring full-time staff</li>
        <li><strong>Consistent Service:</strong> Every caller receives professional attention</li>
        <li><strong>Local Understanding:</strong> Systems can be trained on SA business practices</li>
      </ul>

      <h2>Implementation Is Easier Than You Think</h2>
      <p>Many business owners assume implementing AI technology requires technical expertise. However, modern solutions like Gerbo require no technical knowledge—just a simple call forwarding setup that takes minutes.</p>

      <h2>The ROI Story</h2>
      <p>Let's break down the mathematics: If you're missing just 10 calls per week, and your average client value is R1,500, you're potentially losing R78,000 annually. An AI receptionist service costs a fraction of this—typically under R1,000 per month—while capturing the majority of those missed opportunities.</p>

      <h2>Looking Ahead</h2>
      <p>As AI technology continues to advance, we're seeing increasingly sophisticated capabilities—from appointment booking to CRM integration. For South African businesses, now is the perfect time to adopt this technology before it becomes the industry standard.</p>

      <p>The question isn't whether to implement AI phone answering—it's when. Those who move early will capture the competitive advantage while their competitors are still letting calls go to voicemail.</p>
    `,
    tags: ["AI", "Small Business", "Customer Service", "South Africa"]
  },
  "2": {
    id: 2,
    title: "5 Signs Your Business Is Losing Money from Missed Calls",
    category: "Tips & Insights",
    author: "James Thompson",
    authorBio: "Sales optimization expert with 15 years experience",
    date: "Feb 8, 2026",
    readTime: "4 min read",
    image: "/images/blog-2.jpg",
    content: `
      <p>Are you leaving money on the table without even realizing it? Missed calls are one of the most overlooked sources of revenue loss for businesses. Here are five clear warning signs that your business is suffering from this silent profit killer.</p>

      <h2>1. Your Voicemail Box Is Constantly Full</h2>
      <p>If you're regularly having to delete voicemails to make room for new ones, you're missing calls. Worse, when callers can't even leave a message, they simply move on to your competitor. This is particularly devastating in competitive industries like real estate, healthcare, and professional services.</p>

      <h2>2. You Notice Call Spikes During Busy Hours</h2>
      <p>Check your call logs. Do you see multiple calls coming in during peak hours, particularly calls that go unanswered? These represent potential customers who called when you were helping someone else. They likely hung up and called your competitor instead.</p>

      <h2>3. Customers Say "I Tried Calling Earlier"</h2>
      <p>When clients mention they tried reaching you before, it's a red flag. For every customer who mentions this, there are likely dozens who tried calling but never called back or mentioned it.</p>

      <blockquote>
        "We had no idea how many calls we were missing until we implemented call tracking. It was eye-opening—and expensive." — Linda Patel, Dental Practice Owner
      </blockquote>

      <h2>4. Your Competitors Are Growing Faster</h2>
      <p>If competitors in your area are expanding while you're stagnant, missed calls could be the differentiator. They may have better phone coverage, capturing the leads you're losing.</p>

      <h2>5. After-Hours Inquiries Go Unanswered</h2>
      <p>In today's always-on economy, customers expect some level of after-hours service. If you're completely unreachable outside business hours, you're losing to competitors who are available 24/7.</p>

      <h2>The Real Cost</h2>
      <p>Let's do the math together:</p>
      <ul>
        <li>Average missed calls per week: 10</li>
        <li>Conversion rate if answered: 40%</li>
        <li>Average client value: R2,000</li>
        <li>Annual lost revenue: R416,000</li>
      </ul>

      <h2>The Solution</h2>
      <p>The good news? This problem is completely solvable. AI-powered phone answering systems can capture these calls 24/7 for a fraction of the cost of the revenue you're losing. Solutions like Gerbo ensure every call is answered professionally, details are captured, and you're notified immediately.</p>

      <p>Don't let another day of revenue slip away. The time to act is now.</p>
    `,
    tags: ["Revenue Loss", "Business Tips", "Phone Systems", "Growth"]
  }
};

// Related posts
const relatedPosts = [
  {
    id: 3,
    title: "Case Study: How a Cape Town Estate Agency Increased Leads by 40%",
    category: "Case Studies",
    readTime: "7 min read"
  },
  {
    id: 4,
    title: "The Complete Guide to 24/7 Customer Service",
    category: "Guides",
    readTime: "10 min read"
  },
  {
    id: 6,
    title: "Why Restaurants Are Switching to AI Phone Answering",
    category: "Industry Insights",
    readTime: "5 min read"
  }
];

export const BlogPost: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [post, setPost] = useState<any>(null);

  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo(0, 0);

    // Load post data
    if (id && blogPostsData[id]) {
      setPost(blogPostsData[id]);
    }

    // Header scroll effect
    const handleScroll = () => {
      const header = document.querySelector('.header');
      if (window.scrollY > 50) {
        header?.classList.add('scrolled');
      } else {
        header?.classList.remove('scrolled');
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [id]);

  const toggleMobileMenu = () => {
    const navMenu = document.querySelector('.nav-menu') as HTMLElement;
    if (navMenu.style.display === 'flex') {
      navMenu.style.display = 'none';
    } else {
      navMenu.style.display = 'flex';
      navMenu.style.flexDirection = 'column';
      navMenu.style.position = 'absolute';
      navMenu.style.top = '100%';
      navMenu.style.left = '0';
      navMenu.style.right = '0';
      navMenu.style.background = 'white';
      navMenu.style.padding = '2rem';
      navMenu.style.boxShadow = '0 8px 24px rgba(0, 0, 0, 0.1)';
      navMenu.style.zIndex = '1000';
    }
  };

  const shareOnTwitter = () => {
    const url = window.location.href;
    const text = post?.title || '';
    window.open(`https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`, '_blank');
  };

  const shareOnLinkedIn = () => {
    const url = window.location.href;
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`, '_blank');
  };

  const copyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    alert('Link copied to clipboard!');
  };

  if (!post) {
    return (
      <div style={{ padding: '200px 2rem', textAlign: 'center' }}>
        <h2>Post not found</h2>
        <button onClick={() => navigate('/blog')} className="btn btn-primary" style={{ marginTop: '2rem' }}>
          Back to Blog
        </button>
      </div>
    );
  }

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@300;400;500;600;700;800&family=Poppins:wght@300;400;500;600;700&display=swap');

        :root {
          --font-heading: 'Space Grotesk', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
          --font-body: 'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
          --primary: #0A0E27;
          --accent: #00E5A0;
          --accent-secondary: #00B8D4;
          --text-dark: #0A0E27;
          --text-light: #5A607F;
          --text-muted: #9EA4B8;
          --bg-light: #F8FAFB;
          --bg-white: #fcfadd;
          --border: #E5E9F2;
        }

        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        body {
          font-family: var(--font-body);
          line-height: 1.6;
          color: var(--text-dark);
          background: var(--bg-white);
        }

        /* Article Header */
        .article-header {
          padding: 140px 2rem 60px;
          background: var(--bg-light);
          text-align: center;
        }

        .article-header-content {
          max-width: 900px;
          margin: 0 auto;
        }

        .back-link {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          color: var(--text-light);
          text-decoration: none;
          font-weight: 600;
          margin-bottom: 2rem;
          transition: color 0.3s ease;
        }

        .back-link:hover {
          color: var(--accent);
        }

        .article-category {
          color: var(--accent);
          font-size: 0.95rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          margin-bottom: 1rem;
        }

        .article-title {
          font-family: var(--font-heading);
          font-size: clamp(2rem, 5vw, 3.5rem);
          font-weight: 800;
          line-height: 1.2;
          margin-bottom: 1.5rem;
          letter-spacing: -0.02em;
        }

        .article-meta {
          display: flex;
          gap: 2rem;
          justify-content: center;
          align-items: center;
          flex-wrap: wrap;
          margin-top: 2rem;
          padding-top: 2rem;
          border-top: 2px solid var(--border);
        }

        .article-author {
          display: flex;
          align-items: center;
          gap: 1rem;
        }

        .author-avatar-large {
          width: 56px;
          height: 56px;
          border-radius: 50%;
          background: linear-gradient(135deg, var(--accent) 0%, var(--accent-secondary) 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          color: var(--primary);
          font-weight: 700;
          font-size: 1.2rem;
        }

        .author-details {
          text-align: left;
        }

        .author-name {
          font-weight: 700;
          color: var(--text-dark);
          font-size: 1.05rem;
        }

        .author-bio {
          color: var(--text-muted);
          font-size: 0.9rem;
        }

        .article-date,
        .article-read-time {
          color: var(--text-muted);
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        /* Article Content */
        .article-content-wrapper {
          max-width: 800px;
          margin: 0 auto;
          padding: 80px 2rem;
        }

        .article-image {
          width: 100%;
          height: 500px;
          border-radius: 24px;
          background: linear-gradient(135deg, rgba(0, 229, 160, 0.2) 0%, rgba(0, 184, 212, 0.2) 100%);
          margin-bottom: 4rem;
          overflow: hidden;
        }

        .article-image img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .article-content {
          font-size: 1.15rem;
          line-height: 1.8;
          color: var(--text-dark);
        }

        .article-content p {
          margin-bottom: 1.75rem;
        }

        .article-content h2 {
          font-family: var(--font-heading);
          font-size: 2rem;
          font-weight: 800;
          margin-top: 3rem;
          margin-bottom: 1.25rem;
          letter-spacing: -0.02em;
          line-height: 1.3;
        }

        .article-content h3 {
          font-family: var(--font-heading);
          font-size: 1.5rem;
          font-weight: 700;
          margin-top: 2.5rem;
          margin-bottom: 1rem;
        }

        .article-content ul,
        .article-content ol {
          margin: 1.75rem 0 1.75rem 1.5rem;
        }

        .article-content li {
          margin-bottom: 0.75rem;
          line-height: 1.8;
        }

        .article-content blockquote {
          margin: 2.5rem 0;
          padding: 2rem 2.5rem;
          background: var(--bg-light);
          border-left: 4px solid var(--accent);
          border-radius: 12px;
          font-style: italic;
          font-size: 1.2rem;
          color: var(--text-dark);
        }

        .article-content strong {
          font-weight: 700;
          color: var(--text-dark);
        }

        /* Tags */
        .article-tags {
          display: flex;
          gap: 1rem;
          flex-wrap: wrap;
          margin: 3rem 0;
          padding-top: 2rem;
          border-top: 2px solid var(--border);
        }

        .tag {
          padding: 0.5rem 1.25rem;
          background: rgba(0, 229, 160, 0.1);
          border: 1px solid rgba(0, 229, 160, 0.3);
          border-radius: 50px;
          color: var(--primary);
          font-size: 0.9rem;
          font-weight: 600;
        }

        /* Share Buttons */
        .share-section {
          margin: 3rem 0;
          padding: 2rem;
          background: var(--bg-light);
          border-radius: 16px;
          text-align: center;
        }

        .share-section h3 {
          font-family: var(--font-heading);
          font-size: 1.35rem;
          font-weight: 800;
          margin-bottom: 1.5rem;
        }

        .share-buttons {
          display: flex;
          gap: 1rem;
          justify-content: center;
          flex-wrap: wrap;
        }

        .share-btn {
          padding: 0.875rem 1.75rem;
          border: 2px solid var(--border);
          border-radius: 12px;
          background: var(--bg-white);
          color: var(--text-dark);
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          gap: 0.75rem;
        }

        .share-btn:hover {
          border-color: var(--accent);
          color: var(--accent);
          transform: translateY(-2px);
        }

        /* Related Posts */
        .related-posts {
          padding: 80px 2rem;
          background: var(--bg-light);
        }

        .related-posts-container {
          max-width: 1400px;
          margin: 0 auto;
        }

        .related-posts h2 {
          font-family: var(--font-heading);
          font-size: 2.5rem;
          font-weight: 800;
          text-align: center;
          margin-bottom: 3rem;
        }

        .related-posts-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
          gap: 2rem;
        }

        .related-post-card {
          background: var(--bg-white);
          padding: 2rem;
          border-radius: 20px;
          border: 2px solid var(--border);
          transition: all 0.4s ease;
          cursor: pointer;
        }

        .related-post-card:hover {
          transform: translateY(-8px);
          border-color: var(--accent);
          box-shadow: 0 20px 48px rgba(0, 229, 160, 0.15);
        }

        .related-post-category {
          color: var(--accent);
          font-size: 0.85rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          margin-bottom: 0.75rem;
        }

        .related-post-card h3 {
          font-family: var(--font-heading);
          font-size: 1.35rem;
          font-weight: 800;
          line-height: 1.3;
          margin-bottom: 0.75rem;
        }

        .related-post-meta {
          color: var(--text-muted);
          font-size: 0.9rem;
        }

        /* CTA Section */
        .article-cta {
          padding: 100px 2rem;
          background: var(--primary);
          text-align: center;
          color: var(--bg-white);
        }

        .article-cta h2 {
          font-family: var(--font-heading);
          font-size: clamp(2rem, 4vw, 3rem);
          font-weight: 800;
          margin-bottom: 1.5rem;
        }

        .article-cta p {
          font-size: 1.2rem;
          margin-bottom: 2.5rem;
          opacity: 0.9;
        }

        /* Responsive */
        @media (max-width: 768px) {
          .article-header {
            padding: 120px 1.5rem 40px;
          }

          .article-content-wrapper {
            padding: 60px 1.5rem;
          }

          .article-image {
            height: 300px;
            border-radius: 16px;
          }

          .article-meta {
            flex-direction: column;
            gap: 1rem;
          }

          .related-posts-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>

      {/* Header */}
      <header className="header">
        <nav className="nav-container">
          <a href="/">
            <img src="/images/8EYtClIJaotk40RaLdi5bBD444-1.png" alt="Gerbo Logo" className="logo" />
          </a>
          
          <button className="mobile-menu-toggle" aria-label="Toggle menu" onClick={toggleMobileMenu}>
            ☰
          </button>
          
          <ul className="nav-menu">
            <li><a href="/#features">Features</a></li>
            <li><a href="/#pricing">Pricing</a></li>
            <li><a href="/#industries">Industries</a></li>
            <li><a href="/#faqs">FAQ</a></li>
            <li><a href="/contact">Contact</a></li>
            <li><a href="/blog" style={{ color: 'var(--accent)' }}>Blog</a></li>
            <li>
              <button onClick={() => navigate('/login')} className="btn btn-primary">
                Try Gerbo Live
              </button>
            </li>
          </ul>
        </nav>
      </header>

      {/* Article Header */}
      <section className="article-header">
        <div className="article-header-content">
          <a href="/blog" className="back-link">
            <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7"></path>
            </svg>
            Back to Blog
          </a>
          
          <div className="article-category">{post.category}</div>
          <h1 className="article-title">{post.title}</h1>
          
          <div className="article-meta">
            <div className="article-author">
              <div className="author-avatar-large">
                {post.author.split(' ').map((n: string) => n[0]).join('')}
              </div>
              <div className="author-details">
                <div className="author-name">{post.author}</div>
                <div className="author-bio">{post.authorBio}</div>
              </div>
            </div>
            <div className="article-date">
              <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
              </svg>
              {post.date}
            </div>
            <div className="article-read-time">
              <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
              {post.readTime}
            </div>
          </div>
        </div>
      </section>

      {/* Article Content */}
      <div className="article-content-wrapper">
        <div className="article-image">
          {/* Placeholder gradient - replace with actual image */}
          <div style={{
            width: '100%',
            height: '100%',
            background: 'linear-gradient(135deg, rgba(0, 229, 160, 0.3) 0%, rgba(0, 184, 212, 0.3) 100%)'
          }} />
        </div>

        <article className="article-content" dangerouslySetInnerHTML={{ __html: post.content }} />

        {/* Tags */}
        <div className="article-tags">
          {post.tags.map((tag: string) => (
            <span key={tag} className="tag">#{tag}</span>
          ))}
        </div>

        {/* Share Section */}
        <div className="share-section">
          <h3>Share This Article</h3>
          <div className="share-buttons">
            <button className="share-btn" onClick={shareOnTwitter}>
              <svg width="20" height="20" fill="currentColor" viewBox="0 0 24 24">
                <path d="M23 3a10.9 10.9 0 01-3.14 1.53 4.48 4.48 0 00-7.86 3v1A10.66 10.66 0 013 4s-4 9 5 13a11.64 11.64 0 01-7 2c9 5 20 0 20-11.5a4.5 4.5 0 00-.08-.83A7.72 7.72 0 0023 3z"></path>
              </svg>
              Twitter
            </button>
            <button className="share-btn" onClick={shareOnLinkedIn}>
              <svg width="20" height="20" fill="currentColor" viewBox="0 0 24 24">
                <path d="M16 8a6 6 0 016 6v7h-4v-7a2 2 0 00-2-2 2 2 0 00-2 2v7h-4v-7a6 6 0 016-6zM2 9h4v12H2z"></path>
                <circle cx="4" cy="4" r="2"></circle>
              </svg>
              LinkedIn
            </button>
            <button className="share-btn" onClick={copyLink}>
              <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"></path>
              </svg>
              Copy Link
            </button>
          </div>
        </div>
      </div>

      {/* Related Posts */}
      <section className="related-posts">
        <div className="related-posts-container">
          <h2>Related Articles</h2>
          <div className="related-posts-grid">
            {relatedPosts.map((relatedPost) => (
              <div
                key={relatedPost.id}
                className="related-post-card"
                onClick={() => navigate(`/blog/${relatedPost.id}`)}
              >
                <div className="related-post-category">{relatedPost.category}</div>
                <h3>{relatedPost.title}</h3>
                <div className="related-post-meta">{relatedPost.readTime}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="article-cta">
        <h2>Ready to Transform Your Business?</h2>
        <p>Join 150+ South African businesses using Gerbo to never miss a call again.</p>
        <button onClick={() => navigate('/register')} className="btn btn-primary" style={{ marginRight: '1rem' }}>
          Start Free Trial
        </button>
        <a href="/contact" className="btn btn-outline-white">
          Talk to Sales
        </a>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-container">
          <div className="footer-grid">
            <div className="footer-column">
              <img src="/images/8EYtClIJaotk40RaLdi5bBD444-1.png" alt="Gerbo Logo" className="logo" style={{ marginBottom: '1.5rem' }} />
              <p style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '1rem', lineHeight: 1.7 }}>Your friendly AI receptionist, always available to help your business grow.</p>
            </div>

            <div className="footer-column">
              <h4>Product</h4>
              <ul className="footer-links">
                <li><a href="/#features">Features</a></li>
                <li><a href="/#pricing">Pricing</a></li>
                <li><a href="/#industries">Industries</a></li>
                <li><a href="/blog">Blog</a></li>
              </ul>
            </div>

            <div className="footer-column">
              <h4>Company</h4>
              <ul className="footer-links">
                <li><a href="/contact">Contact</a></li>
                <li><a href="/privacy">Privacy</a></li>
                <li><a href="/terms">Terms</a></li>
              </ul>
            </div>

            <div className="footer-column">
              <h4>Support</h4>
              <ul className="footer-links">
                <li><a href="mailto:support@gerboai.com">support@gerboai.com</a></li>
                
              </ul>
            </div>
          </div>

          <div className="footer-bottom">
            <p>© 2025 Gerbo. All rights reserved.</p>
            <p>Made by Solid5</p>
          </div>
        </div>
      </footer>
    </>
  );
};